"use client";

import { Fragment, useCallback, useRef, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { SheetShell } from "@/components/odds-screen/live/top-menus";

export type CrumbItem = {
  readonly id: string;
  readonly label: string;
  readonly meta?: string;
  readonly icon?: React.ReactNode;
  readonly active: boolean;
  readonly group?: string;
  readonly multiple?: boolean;
  readonly disabled?: boolean;
  readonly locked?: { readonly name: string; readonly tag: React.ReactNode; readonly unlock: () => void };
};

function Chevron({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-[4px] border",
        checked ? "border-primary bg-primary text-primary-foreground" : "border-zinc-600",
      )}
    >
      {checked && (
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
      )}
    </span>
  );
}

export function CrumbMenu({
  title, label, icon, items, notes, isMobile, onPick, multiple = false, header, align = "left", tone = "strong", className,
}: {
  title: string;
  label: string;
  icon?: React.ReactNode;
  items: readonly CrumbItem[];
  notes?: Readonly<Record<string, string>>;
  isMobile: boolean;
  onPick: (id: string) => void;
  multiple?: boolean;
  header?: React.ReactNode;
  align?: "left" | "right";
  tone?: "strong" | "quiet";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const name = `${title}: ${label}`;

  const focusItem = (move: (index: number, count: number) => number, enterAtChecked: boolean) => {
    const options = Array.from(menuRef.current?.querySelectorAll<HTMLElement>("[role^=menuitem], [role=switch]") ?? []);
    if (options.length === 0) return;
    const current = options.indexOf(document.activeElement as HTMLElement);
    const checked = Math.max(0, options.findIndex((option) => option.getAttribute("aria-checked") === "true"));
    const target = current === -1 && enterAtChecked ? checked : move(current === -1 ? checked : current, options.length);
    options[(target + options.length) % options.length].focus();
  };

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, (index: number, count: number) => number> = {
      ArrowDown: (index) => index + 1,
      ArrowUp: (index) => index - 1,
      Home: () => 0,
      End: (_, count) => count - 1,
    };
    if (event.key === "Escape" || event.key === "Tab") {
      close();
      if (event.key === "Escape") triggerRef.current?.focus();
      return;
    }
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    if (!open) setOpen(true);
    const enterAtChecked = event.key === "ArrowDown" || event.key === "ArrowUp";
    requestAnimationFrame(() => focusItem(move, enterAtChecked));
  };

  const row = (item: CrumbItem) => {
    const { locked } = item;
    const many = locked === undefined && (item.multiple ?? multiple);
    return (
      <button
        key={item.id}
        type="button"
        role={locked ? "menuitem" : many ? "menuitemcheckbox" : "menuitemradio"}
        aria-checked={locked ? undefined : item.active}
        aria-haspopup={locked ? "dialog" : undefined}
        aria-label={locked?.name}
        aria-disabled={item.disabled || undefined}
        onClick={item.disabled ? undefined : () => {
          if (locked) locked.unlock();
          else onPick(item.id);
          if (many) return;
          close();
          triggerRef.current?.focus();
        }}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2.5 text-left text-[14px] font-medium",
          isMobile ? "h-11" : "h-9",
          item.disabled ? "cursor-not-allowed text-zinc-500 opacity-60" : item.active && !many ? "bg-primary/10 text-primary" : item.active ? "text-foreground hover:bg-white/[0.06]" : "text-zinc-300 hover:bg-white/[0.06] hover:text-foreground",
        )}
      >
        {many && <CheckBox checked={item.active} />}
        {locked && <span aria-hidden="true" className="flex size-4 shrink-0 items-center justify-center text-zinc-500"><Lock size={12} /></span>}
        {item.icon}
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.meta !== undefined && <span className="data-num text-[11px] text-zinc-500">{item.meta}</span>}
        {locked?.tag}
      </button>
    );
  };

  const sections: { group: string | undefined; items: CrumbItem[] }[] = [];
  for (const item of items) {
    const last = sections[sections.length - 1];
    if (last !== undefined && last.group === item.group) last.items.push(item);
    else sections.push({ group: item.group, items: [item] });
  }
  const list = sections.map((section, index) => section.group === undefined
    ? <Fragment key={index}>{section.items.map(row)}</Fragment>
    : (
      <div key={index} role="group" aria-label={section.group}>
        <div aria-hidden="true" className="px-2.5 pb-1 pt-2 text-[10px] font-medium uppercase tracking-[0.08em] text-zinc-500">{section.group}</div>
        {notes?.[section.group] !== undefined && <p className="px-2.5 pb-1 text-[11px] text-zinc-500">{notes[section.group]}</p>}
        {section.items.map(row)}
      </div>
    ));

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={name}
          onClick={() => setOpen(true)}
          className={cn("flex h-11 min-w-0 items-center gap-1.5 rounded-lg pr-1 text-left", className)}
          style={{ fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif", fontWeight: 700 }}
        >
          {icon}
          <span className={cn("truncate text-foreground", tone === "strong" ? "text-[17px]" : "text-[14px] font-semibold text-zinc-300")}>{label}</span>
          <span className="flex text-zinc-500"><Chevron size={16} /></span>
        </button>
        <SheetShell open={open} title={title} onClose={close}>
          <div className="min-h-0 flex-grow overflow-y-auto px-2 pb-[max(1rem,env(safe-area-inset-bottom))]">{header}{list}</div>
        </SheetShell>
      </>
    );
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <div className={cn("relative flex-shrink-0", className)} onKeyDown={onMenuKeyDown}>
        <PopoverPrimitive.Trigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={name}
            className={cn(
              "tap flex h-8 items-center gap-1.5 rounded-md px-1.5 text-[13px] hover:bg-white/[0.04]",
              tone === "strong" ? "font-semibold text-foreground" : "font-medium text-zinc-300 hover:text-foreground",
              open && "bg-white/[0.06]",
            )}
          >
            {icon}
            <span className="whitespace-nowrap">{label}</span>
            <span className="flex text-zinc-500"><Chevron /></span>
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Content
          ref={menuRef}
          role="menu"
          aria-label={title}
          side="bottom"
          align={align === "right" ? "end" : "start"}
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-30 max-h-[min(460px,var(--radix-popover-content-available-height))] w-56 origin-[var(--radix-popover-content-transform-origin)] overflow-y-auto rounded-lg border border-surface-line bg-surface-overlay p-1 shadow-xl outline-none animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none"
        >
          {header}
          {list}
        </PopoverPrimitive.Content>
      </div>
    </PopoverPrimitive.Root>
  );
}
